import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { companyInfo, coverageInterestOptions, contactTimeOptions } from '../utils/mockData';
import { submitContact } from '../utils/api';
import { toast } from 'sonner';

const Contact = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverageInterests: [],
    careerInterest: false,
    preferredContact: '',
    preferredTime: '',
    message: ''
  });

  const handleCheckbox = (value) => {
    setFormData(prev => ({
      ...prev,
      coverageInterests: prev.coverageInterests.includes(value)
        ? prev.coverageInterests.filter(v => v !== value)
        : [...prev.coverageInterests, value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await submitContact({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        subject: `${formData.careerInterest ? '[CAREER INTEREST] ' : ''}Coverage Interest: ${formData.coverageInterests.join(', ') || 'General'}`,
        message: `${formData.careerInterest ? 'CAREER INTEREST: Interested in becoming an agent\n' : ''}Preferred Contact: ${formData.preferredContact || 'Any'}\nPreferred Time: ${formData.preferredTime || 'Any'}\n\n${formData.message}`,
        coverageInterests: formData.coverageInterests,
        careerInterest: formData.careerInterest,
        preferredContact: formData.preferredContact,
        preferredTime: formData.preferredTime
      });
      if (result.success) {
        toast.success('Message sent successfully!');
        setTimeout(() => navigate('/thank-you'), 2000);
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative bg-cover bg-center py-12"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 71, 171, 0.9), rgba(0, 71, 171, 0.9)), url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1200)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Let&apos;s Find the Right Coverage for You</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">Have questions about insurance coverage? Need help comparing options? Contact Purposefully Covered Insurance Group today - we&apos;re here to help you get covered with purpose.</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-brand-blue mb-6">Send Us A Message</h2>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text" required value={formData.fullName}
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="Your full name"
                      data-testid="contact-fullname"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email" required value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        placeholder="you@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <input
                        type="tel" required value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        placeholder="(912) 376-9006"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  {/* Coverage Interest Checkboxes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Interest</label>
                    <div className="grid grid-cols-2 gap-2">
                      {["Life Insurance", "Final Expense", "Health & Supplemental", "Medicare", "IULs", "Annuities", "Dental", "Vision"].map(option => (
                        <label key={option} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.coverageInterests.includes(option)}
                            onChange={() => handleCheckbox(option)}
                            className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Career Interest */}
                  <div className="bg-brand-blue/5 border border-brand-blue/20 rounded-md p-4" data-testid="career-interest-section">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.careerInterest}
                        onChange={(e) => setFormData({...formData, careerInterest: e.target.checked})}
                        className="mt-1 rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                        data-testid="contact-career-interest"
                      />
                      <span className="text-sm text-gray-700">
                        <span className="block font-semibold text-brand-blue">Interested in a career as an insurance agent?</span>
                        Check this box if you&apos;re reaching out about becoming an agent. David Ashley will personally follow up about the opportunity.
                      </span>
                    </label>
                  </div>

                  {/* Preferred Contact Method */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact Method</label>
                      <select
                        value={formData.preferredContact}
                        onChange={(e) => setFormData({...formData, preferredContact: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                        data-testid="contact-method"
                      >
                        <option value="">No preference</option>
                        <option value="Phone">Phone</option>
                        <option value="Text">Text</option>
                        <option value="Email">Email</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact Time</label>
                      <select
                        value={formData.preferredTime}
                        onChange={(e) => setFormData({...formData, preferredTime: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                        data-testid="contact-time"
                      >
                        <option value="">No preference</option>
                        {contactTimeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="Tell us about your insurance needs..."
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full bg-brand-orange text-white px-6 py-4 rounded-md hover:bg-brand-blue transition font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-brand-blue mb-6">Get In Touch</h2>
                <p className="text-base text-gray-700 mb-8 leading-relaxed">
                  Have questions about insurance coverage? Need a quote? Want to discuss your options?
                  We&apos;re here to help. Reach out today!
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-brand-blue text-white p-3 rounded-full">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-brand-blue mb-1">Phone</h3>
                      <a href={`tel:${companyInfo.phoneRaw}`} className="text-gray-700 hover:text-brand-orange transition" data-testid="contact-phone-link">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-brand-blue text-white p-3 rounded-full">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-brand-blue mb-1">Email</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-gray-700 hover:text-brand-orange transition">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-brand-blue text-white p-3 rounded-full">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-brand-blue mb-1">Mailing Address</h3>
                      <p className="text-gray-700 font-semibold">{companyInfo.name}</p>
                      <p className="text-gray-700">{companyInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-brand-blue text-white p-3 rounded-full">
                      <Clock size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-brand-blue mb-1">Hours</h3>
                      <p className="text-gray-700">{companyInfo.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-bold text-lg text-brand-blue mb-3">Prefer to Schedule a Call?</h3>
                  <p className="text-gray-700 mb-4 text-sm">
                    Let us know a good time to reach you, and one of our licensed agents will call at your convenience.
                  </p>
                  <a
                    href={`tel:${companyInfo.phoneRaw}`}
                    className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-md hover:bg-brand-orange-light transition font-semibold"
                    data-testid="contact-callback-btn"
                  >
                    <Phone size={18} /> Call {companyInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
